import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Send } from "lucide-react";
import { Container, Reveal, SectionHeading, PageBanner, siteImg } from "@/site/ui";

export default function GetQuote() {
  const c = useOutletContext();
  const q = c.quote || {};
  const services = q.services || [];
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: services[0] || "", location: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Please enter your name and phone number"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/site/quote", form);
      toast.success(data.message || q.success_message || "Thank you — we'll be in touch shortly.");
      setForm({ name: "", phone: "", email: "", service: services[0] || "", location: "", message: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const input = "w-full rounded-xl border border-[#E5E5E5] bg-white p-4 text-sm text-[#0A0A0A] placeholder:text-[#A3A3A3] outline-none transition-colors duration-200 focus:border-[var(--rgh-primary)]";

  return (
    <div data-testid="get-quote-page">
      <PageBanner image={c.banners?.quote} eyebrow="Get A Quote" title={q.heading || "Request a quote"} subtitle={q.subtitle} />

      <section className="py-16 md:py-24">
        <Container className="grid lg:grid-cols-5 gap-10 items-start">
          <Reveal className="lg:col-span-2">
            <SectionHeading eyebrow="No obligation" title="Tell us what you need" sub={q.subtitle} />
            {q.image && <img src={siteImg(q.image)} alt="" className="mt-8 w-full rounded-2xl object-cover aspect-[4/3] border border-gray-100" />}
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-3">
            <form onSubmit={submit} data-testid="quote-form" className="rounded-[28px] border border-gray-100 bg-white p-6 sm:p-8 shadow-sm space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-2 text-[#525252]">Full name *</label>
                  <input data-testid="quote-name" className={input} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-2 text-[#525252]">Phone *</label>
                  <input data-testid="quote-phone" className={input} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+234 …" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-2 text-[#525252]">Email</label>
                  <input data-testid="quote-email" className={input} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-2 text-[#525252]">Location</label>
                  <input data-testid="quote-location" className={input} value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City / State" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-2 text-[#525252]">What do you need a quote for?</label>
                <select data-testid="quote-service" className={input} value={form.service} onChange={(e) => set("service", e.target.value)}>
                  {services.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-2 text-[#525252]">Message / details</label>
                <textarea data-testid="quote-message" rows={4} className={input} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us more about your requirement…" />
              </div>
              <button type="submit" disabled={busy} data-testid="quote-submit"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--rgh-primary)] px-7 py-3.5 text-sm font-semibold text-white transition-[transform,background-color,box-shadow] duration-200 hover:bg-[var(--rgh-primary-dark)] hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60">
                <Send size={16} /> {busy ? "Sending…" : "Request my quote"}
              </button>
            </form>
          </Reveal>
        </Container>
      </section>
    </div>
  );
}
